/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { APP_CONFIG as CORE_APP_CONFIG } from '@core/config/app-config';
import { AppConfigModel } from '@models/app/app-config.model';
import { EditableParam } from '@core/models/quotation-calculate';
import { HANDLER } from '@core/models';

export const APP_CONFIG: AppConfigModel = {
  ...CORE_APP_CONFIG,
  generic: {
    ...CORE_APP_CONFIG.generic,
    errorDisplay: 'page',
    blockingErrorCodes: ['500', '300'],
    warningErrorCodes: ['200'],
    unhandledErrorCodeAs: '300',
    errorIcons: {
      ['500']: 'assistance',
      ['300']: 'warning-error',
      ['200']: 'warning-error'
    },
    contactsNeedChecking: ['SMS'],
    retryBtnAppearance: 'solid'
  },
  theme: {
    ...CORE_APP_CONFIG.theme,
    logoOnHeader: false,
    logoUrl: 'assets/icons/logo_banca.svg',
    footerPrimaryText: 'btn.continue',
    tooltipInterface: 'modal',
    activeViews: [
      ['WIZARD_STEP_NUMBER', true],
      ['UPL_DOC_SUCC_TOAST', true],
      ['SIGN_PREFACE', true]
    ],
    confirmationInterface: 'modal',
    srcAlias: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      './assets/icons/a_identification_card.svg':
        './assets/icons/identification_card.svg',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      './assets/icons/b_health_card.svg': './assets/icons/health_card.svg'
    }
  },
  security: {
    ...CORE_APP_CONFIG.security,
    authCodeRequired: false,
    hideSender: true,
    dynamicAuthCodeEngage: true,
    authStrategy: 'header',
    urlOutOfAuth: [
      'assets/i18n',
      'assets/res',
      'assets/demo',
      'qinetic-security/api/authenticate'
    ]
  },
  recognition: {
    spidOptions: {
      providersChoice: true,
      handler: HANDLER.experian,
      providerResourceUrl: './assets/res/entities-idp.json'
    }
  },
  position: {
    ...CORE_APP_CONFIG.position,
    preventNavOfConcludedPosition: true,
    preventNavOfRetreatPosition: true,
    considerPositionConcludedWhen: [
      '350|KO-AVR',
      '350|KO-CAMS',
      '350|KO-PROCESSOR',
      '350|KO-OBI',
      '350|KO-SETUP',
      '300|STANDBY',
      '400|CARD-GIVEN'
    ],
    usePollingOnRelease: true
  },
  wizard: {
    ...CORE_APP_CONFIG.wizard,
    startPage: 'first-open-step',
    closingMode: 'wizard',
    nextPage: 'first-open-step',
    avoidBack: true,
    freeNavigation: true,
    progressBar: 'relative',
    checkResumeOn: ['PRIMARY_DATA'],
    progressBarGroups: ['GROUP_B'],
    allowStateMenu: false,
    stepsWithCloseMessage: ['PHONE', 'CONTRACT_SIGNATURE'],
    stopAllowedOn: [['PRIMARY_DATA']],
    appendStepPathParam: true,
    reloadPositionOn: ['INTRO']
  },
  upload: {
    ...CORE_APP_CONFIG.upload,
    avoidWhichPage: false,
    avoidInstructions: true,
    documentPages: {
      ['ID_CARD']: 'front|back',
      ['PASSPORT']: 'front',
      ['DRIVER_LICENSE']: 'front|back',
      ['ID_CARD_CART']: 'front|back',
      ['CF_CODICEFISCALE']: 'front|back'
    },
    additionalPages: {},
    documentPictures: ['ID_CARD', 'PASSPORT', 'DRIVER_LICENSE', 'ID_CARD_CART'],
    uploadSorting: ['DOCUMENTI_UTENTE', 'CF_CODICEFISCALE'],
    checkSorting: ['DOCUMENTI_UTENTE', 'CF_CODICEFISCALE'],
    acquisitionMode: 'mitek',
    mitekLicense:
      // eslint-disable-next-line max-len
      'eyJzaWduYXR1cmUiOiJrY3R1YkUzWlZNZE9qb1dCd1l2ZmJjd3RZYWFieENNcjkzYktKM1VDL0dWR0lCdk9CVFZjRVpHK08zc0FQNzlaUkQ1Y2U1S1doVnVOS05oNXhHTm1HR0NBeEw0T2tlQzE3aEo2OUdDRHZBcXhyY0doUVRUb2xDUDdiOE9qUHVFVm12enJ0c29nbVlSYUNGNUxVa0p4SDRwK3d6SVJESmtSME5jcFYvcElZSGxaUis1ZkxERi9TTGVxWUovcDZLdkVUejl0UzNzMTk4UzcxdWxOZ2FINzlHY2pPN3E1TzFNSU80SnRtdXZqS1RwR1FDb00rbEdOWVNtMUdjMTk1b0tON3pVWVZodXZ0My9VVFQ2ZEFNVitXSkIybUl6T2dDeEtud0R2R2ljZEgzM2dlazZJa3VodDRIQUtTQ2dmcGprUDNUUVBXdG9ubElib0lYZDlITC9qM3c9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI1LTAxLTAxIiwiZmVhdHVyZXMiOlsiZ2VuZXJpYyIsImlkIiwiZmFjZSJdLCJhcHBsaWNhdGlvbklkZW50aWZpZXIiOnsid2ViIjpbInFkZXYwOC5xdWlkaW5mby5pdCIsImlkZW50aXRhZGlnaXRhbGV0ZXN0LmV4cGVyaWFuLml0IiwiaWRlbnRpdGFkaWdpdGFsZS5leHBlcmlhbi5pdCJdfSwiZ3BvIjoiMTZkIiwib3JnYW5pemF0aW9uIjoiTWl0ZWsgU3lzdGVtcyBJbmMuIiwidmVyc2lvbiI6IjEuMCJ9fQ==',
    translateFileName: true,
    runScan: true,
    forceJpg: true
  },
  form: {
    ...CORE_APP_CONFIG.form,
    stepCodeAsCode: true,
    fixedCol: true,
    hideMandatorySign: true,
    selectInterface: 'action-sheet',
    searchInterface: 'modal',
    calendarInterface: 'action-sheet',
    dateFormat: 'iso',
    displayDocOptional: true
  },
  survey: {
    ...CORE_APP_CONFIG.survey,
    alertWhenContinue: false,
    stepCodeAsCode: true,
    fixedCol: true,
    selectInterface: 'action-sheet',
    searchInterface: 'modal',
    avoidReferenceId: true,
    displayDocOptional: true
  },
  dashboard: {
    ...CORE_APP_CONFIG.dashboard
  },
  quotation: {
    ...CORE_APP_CONFIG.quotation,
    primaryData: [
      {
        id: 'requestedAmount',
        title: 'lbl.quotation_upright',
        format: 'currency'
      },
      {
        id: 'durataMassima',
        title: 'lbl.quotation_max_duration',
        format: 'month'
      },
      {
        id: 'rata',
        title: 'lbl.quotation_max_installment',
        format: 'currency'
      },
      { id: 'tan', title: 'lbl.quotation_tan', format: 'perc' },
      { id: 'taeg', title: 'lbl.quotation_taeg', format: 'perc', bold: true }
    ],
    editableParams: [EditableParam.instalment],
    view: 'inline',
    rejectAllowed: false,
    moduleCodes: ['comm-offer', 'pre-contr'],
    moduleCodesToView: ['comm-offer']
  },
  publisher: {
    ...CORE_APP_CONFIG.publisher,
    hideExtension: true,
    downloadDirectly: false
  },
  quotationCalculate: {
    ...CORE_APP_CONFIG.quotationCalculate,
    preparationSections: [
      EditableParam.finality,
      EditableParam.amount,
      EditableParam.instalment
    ],
    editableSections: [
      EditableParam.instalment,
      EditableParam.amount,
      EditableParam.duration
    ]
  },
  documentsRequest: {
    ...CORE_APP_CONFIG.documentsRequest,
    checkFormPrefix: 'SB_',
    /** è un espediente per fare in modo che non vengano richiesti i formi di validazione per i documenti */
    identifyToValidateWithAttribute: 'NO_VALIDATION'
  },
  digitalSignature: {
    ...CORE_APP_CONFIG.digitalSignature,
    pollingFrequency: 3,
    pollingMaxTime: 30
  },
  analytics: {
    ...CORE_APP_CONFIG.analytics,
    enabled: true
  }
};
